import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { useAppSelector } from '@/store/hooks';

import { UpdateReviewTypeParams } from './api/updateReviewType';
import { determineReviewTypeDesc } from './components/ReviewType/utils/determineReviewTypeDesc';
import {
  Filters as FiltersType,
  PatientWorkQueueItem,
  PatientWorkQueueSettings,
  Reviewer,
} from './PatientWorkQueue.types';
import {
  MANUAL,
  PHYSICIAN_ADVISOR,
  STATUS_DETERMINATION,
} from './PatientWorkQueueTable.constants';
import { applyFiltersToPatients } from './utils/applyFiltersToPatients';
import { mapBEGridSettings } from './utils/mapBEGridSettings';
import { sortByP2PDate, sortByTAT } from './utils/sortQueueData';

export type UpdateAssignmentPayload = {
  admissionId: PatientWorkQueueItem['admission_id'];
  assignedUser: PatientWorkQueueItem['assigned_username'] | null;
  currentProfileUserName: string;
  entityId: PatientWorkQueueItem['entity_id'];
};

export type RemovePatientPayload = {
  admissionId: PatientWorkQueueItem['admission_id'];
  currentProfileUserName: string;
  entityId: PatientWorkQueueItem['entity_id'];
};

export type SetPatientsPayload = {
  isXsolisUser: boolean;
  patients: PatientWorkQueueItem[];
};

export type SetReviewersPayload = {
  count: number;
  reviewers: Reviewer[];
};

export type SetFiltersPayload = {
  filters: FiltersType[];
  userName: string;
};

export type UpdateReviewTypePayload = UpdateReviewTypeParams & {
  currentProfileUserName: string;
};

export type SetPaginationPayload = {
  page: number;
  pageSize: number;
};

export type SetPaginationTotalsPayload = {
  count: number;
};

export type PatientWorkQueueState = {
  activeRowIndex: Nullable<number>;
  displayRowsCount: number;
  error: unknown;
  filteredPatients: PatientWorkQueueItem[];
  filters: FiltersType[];
  loading: boolean;
  pagination: {
    offset: number;
    page: number;
    pageCount: number;
    pageSize: number;
    totalCount: number;
  };
  patients: PatientWorkQueueItem[];
  reviewers: Reviewer[];
  settings: PatientWorkQueueSettings;
};

const initialState: PatientWorkQueueState = {
  activeRowIndex: null,
  displayRowsCount: 0,
  error: null,
  filteredPatients: [],
  filters: [],
  loading: true,
  pagination: {
    offset: 0,
    page: 0,
    pageCount: 0,
    pageSize: 100,
    totalCount: 0,
  },
  patients: [],
  reviewers: [],
  settings: {
    columnsState: [],
  },
};

export const patientWorkQueueSlice = createSlice({
  initialState,
  name: 'patientWorkQueue',
  reducers: {
    CLEAR_FILTERS: (state) => {
      state.filteredPatients = state.patients;
      state.filters = [];
    },
    LOADING: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    REJECTED: (state, action: PayloadAction<unknown>) => {
      state.error = action.payload;
    },
    REMOVE_PATIENT: (state, action: PayloadAction<RemovePatientPayload>) => {
      const { admissionId, currentProfileUserName, entityId } = action.payload;

      const patientIndex = state.patients.findIndex(
        (patient) =>
          patient.entity_id === entityId && patient.admission_id === admissionId
      );

      state.patients.splice(patientIndex, 1);

      state.filteredPatients = applyFiltersToPatients(
        state.patients,
        state.filters,
        currentProfileUserName
      );
    },
    SET_ACTIVE_ROW: (state, action: PayloadAction<Nullable<number>>) => {
      state.activeRowIndex = action.payload;
    },
    SET_DISPLAY_ROWS_COUNT: (state, action: PayloadAction<number>) => {
      state.displayRowsCount = action.payload;
    },
    SET_FILTERS: (state, action: PayloadAction<SetFiltersPayload>) => {
      const { filters, userName } = action.payload;

      state.filters = filters;

      state.filteredPatients = applyFiltersToPatients(
        state.patients,
        filters,
        userName
      );
    },
    SET_PAGINATION: (state, action: PayloadAction<SetPaginationPayload>) => {
      const { page, pageSize } = action.payload;

      state.pagination.pageSize = pageSize;
      state.pagination.page = page;
      state.pagination.offset = page * pageSize;
      state.pagination.pageCount = Math.ceil(
        state.pagination.totalCount / pageSize
      );
    },
    SET_PAGINATION_TOTALS: (
      state,
      action: PayloadAction<SetPaginationTotalsPayload>
    ) => {
      const { count } = action.payload;

      state.pagination.totalCount = count;
      state.pagination.pageCount = Math.ceil(count / state.pagination.pageSize);
    },
    SET_PATIENTS: (state, action: PayloadAction<SetPatientsPayload>) => {
      const { isXsolisUser, patients } = action.payload;

      const sortedPatients = isXsolisUser
        ? patients.toSorted(sortByTAT)
        : patients.toSorted(sortByP2PDate);

      state.patients = sortedPatients;
      state.filteredPatients = sortedPatients;
    },
    SET_REVIEWERS: (state, action: PayloadAction<SetReviewersPayload>) => {
      const { reviewers } = action.payload;

      state.reviewers = reviewers;
    },
    SET_SETTINGS: (state, action: PayloadAction<PatientWorkQueueSettings>) => {
      state.settings.columnsState = action.payload?.columnsState
        ? mapBEGridSettings(action.payload?.columnsState)
        : [];
    },
    UPDATE_ASSIGNMENT: (
      state,
      action: PayloadAction<UpdateAssignmentPayload>
    ) => {
      const { admissionId, assignedUser, currentProfileUserName, entityId } =
        action.payload;

      const patient = state.patients.find(
        (patient) =>
          patient.entity_id === entityId && patient.admission_id === admissionId
      ) as PatientWorkQueueItem;

      patient.assigned_username = assignedUser;

      state.filteredPatients = applyFiltersToPatients(
        state.patients,
        state.filters,
        currentProfileUserName
      );
    },
    UPDATE_REVIEW_TYPE: (
      state,
      action: PayloadAction<UpdateReviewTypePayload>
    ) => {
      const {
        currentProfileUserName,
        isXsolisUser,
        patientQueueId,
        reviewTypeRequestedId,
        value,
      } = action.payload;

      const patient = state.patients.find(
        (patient) => patient.patient_queue_id === patientQueueId
      ) as PatientWorkQueueItem;

      patient.escalating_review_type_id = reviewTypeRequestedId;
      patient.escalating_review_type_desc =
        value === MANUAL ? null : determineReviewTypeDesc(value, isXsolisUser);
      patient.review_type_requested =
        value === STATUS_DETERMINATION ? PHYSICIAN_ADVISOR : value;

      state.filteredPatients = applyFiltersToPatients(
        state.patients,
        state.filters,
        currentProfileUserName
      );
    },
  },
});

export const patientWorkQueueReducer = patientWorkQueueSlice.reducer;
export const patientWorkQueueActions = patientWorkQueueSlice.actions;

export const usePatientWorkQueueSelector = () =>
  useAppSelector((state) => state.patientWorkQueue);
